import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import ScheduleIcon from '@material-ui/icons/Schedule';
import SendIcon from '@material-ui/icons/Send';
import { makeStyles } from '@material-ui/core/styles';

import { contactsLabel } from '../../../constants/whatsapp';
import { actions as whatsappActions } from '../actions';
import { colors } from '../theme';

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...whatsappActions }, dispatch);

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 0),
  },
  label: {
    color: theme.palette.text.secondary,
  },
  value: {
    fontWeight: 500,
    textAlign: 'right',
  },
  progressWrap: {
    paddingTop: theme.spacing(1),
  },
  progress: {
    height: '10px',
    borderRadius: '5px',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  note: {
    color: colors.textDim,
    fontSize: '12px',
    marginTop: theme.spacing(1.5),
    lineHeight: 1.45,
  },
  sendButton: {
    backgroundColor: colors.accent,
    color: '#ffffff',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: colors.accentHover,
    },
  },
  resultOk: {
    color: colors.okText,
    fontWeight: 600,
  },
  resultWarn: {
    color: colors.warnText,
    fontWeight: 600,
  },
  resultError: {
    color: '#d32f2f',
    marginTop: theme.spacing(1),
  },
  leftOut: {
    color: colors.warnText,
    fontSize: '13px',
    lineHeight: 1.5,
    marginTop: theme.spacing(0.5),
  },
  quietBox: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    borderRadius: '8px',
    backgroundColor: colors.warnBg,
    color: colors.warnText,
    fontSize: '13px',
    lineHeight: 1.45,
    '& .MuiFormControlLabel-root': { marginTop: theme.spacing(0.5) },
    '& .MuiFormControlLabel-label': { fontSize: '13px' },
  },
}));

// Кого сервер оставит без сообщения и почему: показываем до нажатия «Отправить».
const leftOutLines = (preview) =>
  [
    [preview.declined, 'отказались от рассылки'],
    [preview.noConsent, 'нет подтверждённого согласия'],
    [preview.notAllowed, 'вне разрешённых номеров тестового режима'],
    [preview.capped, 'уже получили максимум сообщений за неделю'],
    [preview.notAsked, 'недавно уже получили запрос согласия или ответили на него'],
    [preview.duplicates, 'повторы номера'],
  ]
    .filter(([count]) => count > 0)
    .map(([count, text]) => `${text} — ${count}`);

const FINISHED = ['done', 'failed', 'cancelled', 'paused'];
const POLL_MS = 1000;

// One key per opening of the dialog: a second click, or a retry after a lost answer,
// reaches the same mailing instead of creating another one.
const newRequestKey = () => {
  const raw =
    typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return raw.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
};

// Окно отправки: сначала сводка и подтверждение, потом ход отправки. Рассылка идёт на
// сервере в фоне, поэтому окно можно закрыть: она продолжится сама.
function SendBroadcastDialog(props) {
  const classes = useStyles();
  const { open, onClose, onConfirmed, summary } = props;

  // confirm -> progress -> done, or confirm -> scheduled
  const [phase, setPhase] = useState('confirm');
  const [busy, setBusy] = useState(false);
  const [broadcast, setBroadcast] = useState(null);
  const [error, setError] = useState('');
  // Что сделает отправка на самом деле (ответ сервера), и согласие отправить ночью.
  const [preview, setPreview] = useState(null);
  const [quietOk, setQuietOk] = useState(false);
  const [quietAsked, setQuietAsked] = useState(false);
  const requestKey = useRef(newRequestKey());

  useEffect(() => {
    if (open) {
      requestKey.current = newRequestKey();
    } else {
      setPhase('confirm');
      setBusy(false);
      setBroadcast(null);
      setError('');
      setPreview(null);
      setQuietOk(false);
      setQuietAsked(false);
    }
  }, [open]);

  // Пока окно открыто на подтверждении, спрашиваем сервер, что будет на самом деле.
  useEffect(() => {
    if (!open) return undefined;
    let stopped = false;
    props
      .previewBroadcast({
        recipients: summary.recipientList,
        template: {
          name: summary.template.templateName,
          language: summary.template.language,
          consentButtons: Boolean(summary.template.consentButtons),
        },
        ...(summary.scheduled ? summary.schedulePayload : {}),
      })
      .then((result) => {
        if (!stopped && result.ok) setPreview(result.data);
      });
    return () => {
      stopped = true;
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Следим за ходом отправки, пока она не закончится.
  const broadcastId = broadcast && broadcast.id;
  useEffect(() => {
    if (phase !== 'progress' || !broadcastId) return undefined;
    let stopped = false;
    const poll = async () => {
      const result = await props.fetchBroadcast(broadcastId);
      if (stopped || !result.ok) return;
      setBroadcast(result.data);
      if (FINISHED.includes(result.data.status)) {
        setPhase('done');
      }
    };
    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [phase, broadcastId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Пауза нажата, пока уходило очередное сообщение: оно ещё доходит. Пару секунд досматриваем
  // счётчики, чтобы окно не показало «отправлено 1», когда на деле ушло 2.
  const pausedId = phase === 'done' && broadcast && broadcast.status === 'paused' ? broadcast.id : null;
  useEffect(() => {
    if (!pausedId) return undefined;
    let stopped = false;
    let last = '';
    let steady = 0;
    const timer = setInterval(async () => {
      const result = await props.fetchBroadcast(pausedId);
      if (stopped || !result.ok) return;
      setBroadcast(result.data);
      const now = `${result.data.sent}/${result.data.failed}/${result.data.stopped}`;
      steady = now === last ? steady + 1 : 0;
      last = now;
      if (steady >= 2) clearInterval(timer);
    }, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [pausedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = async () => {
    setBusy(true);
    setError('');
    const result = await props.sendBroadcast({
      recipients: summary.recipientList,
      template: {
        name: summary.template.templateName,
        language: summary.template.language,
        consentButtons: Boolean(summary.template.consentButtons),
      },
      name: summary.name,
      listName: summary.listName,
      requestKey: requestKey.current,
      ...(quietOk ? { allowQuietHours: true } : {}),
      ...(summary.scheduled ? summary.schedulePayload : {}),
    });
    setBusy(false);

    if (!result.ok) {
      // Ночь: сервер не отправил и просит подтверждения, галочка появится ниже.
      if (result.code === 'QUIET_HOURS') setQuietAsked(true);
      setError(result.error);
      return;
    }
    setBroadcast(result.data);
    setPhase(result.data.status === 'scheduled' ? 'scheduled' : 'progress');
  };

  const handleStop = async () => {
    setBusy(true);
    const result = await props.pauseBroadcast(broadcast.id);
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
    }
  };

  // Закрыть после создания рассылки = форма очищается, а рассылка идёт дальше.
  const handleClose = () => {
    if (phase === 'confirm') {
      onClose();
    } else {
      onConfirmed(broadcast);
    }
  };

  const recipients = summary.recipients;
  const total = (broadcast && broadcast.total) || recipients;
  const handled = broadcast ? broadcast.sent + broadcast.failed + broadcast.stopped : 0;

  // Сколько получат на самом деле: список мог измениться, пока форма была открыта.
  const actual = preview && !preview.blocked ? preview.willSend : null;
  const quietNeeded = Boolean((preview && preview.quiet) || quietAsked);
  const blocked = Boolean(preview && preview.blocked);
  const cost =
    summary.cost !== null && actual !== null && recipients > 0
      ? ((Number(summary.cost) * actual) / recipients).toFixed(2)
      : summary.cost;

  const rows = [
    { label: 'Шаблон', value: summary.template ? summary.template.title : '' },
    { label: 'Список', value: summary.listName },
    {
      label: summary.welcome ? 'Получат запрос согласия' : 'Получат сообщение',
      value: contactsLabel(actual !== null ? actual : recipients),
    },
    { label: 'Канал', value: 'WhatsApp Business' },
    {
      label: summary.scheduled ? 'Запланировано на' : 'Отправка',
      value: summary.scheduled ? summary.sendAt : 'Сейчас',
    },
    {
      label: 'Ориентировочная стоимость',
      value: cost !== null ? `≈ ${cost} грн` : 'не известна',
    },
  ];

  const title = {
    confirm: summary.scheduled ? 'Запланировать рассылку?' : 'Отправить рассылку?',
    progress: 'Идёт отправка',
    scheduled: broadcast && broadcast.count > 1 ? 'Рассылки запланированы' : 'Рассылка запланирована',
    done: 'Рассылка завершена',
  }[phase];

  return (
    <Dialog open={open} onClose={busy || phase === 'progress' ? undefined : handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary">
          {summary.name}
        </Typography>
        <Divider style={{ margin: '12px 0' }} />

        {phase === 'confirm' && (
          <>
            {rows.map((row) => (
              <Grid key={row.label} className={classes.row}>
                <Typography variant="body2" className={classes.label}>
                  {row.label}
                </Typography>
                <Typography variant="body2" className={classes.value}>
                  {row.value}
                </Typography>
              </Grid>
            ))}
            {preview && leftOutLines(preview).length > 0 && (
              <Typography className={classes.leftOut}>
                Из выбранных {preview.requested} без сообщения останутся: {leftOutLines(preview).join('; ')}.
              </Typography>
            )}
            {blocked && (
              <Typography variant="body2" className={classes.resultError}>
                {preview.blocked.message}
              </Typography>
            )}
            {quietNeeded && !blocked && (
              <div className={classes.quietBox}>
                {/* Текст отдельным блоком: галочка всегда с новой строки, как бы длинно ни было. */}
                <div>
                  {(preview && preview.quietMessage) ||
                    (summary.scheduled
                      ? 'Выбранное время попадает на ночные часы: клиенты не любят сообщения ночью.'
                      : 'Сейчас ночное время: клиенты не любят сообщения ночью.')}
                </div>
                <FormControlLabel
                  control={<Checkbox color="primary" checked={quietOk} onChange={(e) => setQuietOk(e.target.checked)} />}
                  label={summary.scheduled ? 'Всё равно запланировать' : 'Всё равно отправить'}
                />
              </div>
            )}
            {summary.costNote && <Typography className={classes.note}>{summary.costNote}</Typography>}
            {summary.scheduled && (
              <Typography className={classes.note}>
                Время указано по Киеву. Рассылка уйдёт сама, даже если закрыть эту
                страницу: нужно, чтобы работал сервер.
                {summary.scheduleCount > 1
                  ? ' Каждая отправка серии будет отдельной рассылкой в разделе «История»: остановить, изменить или удалить можно одну или всю серию.'
                  : ' Остановить, изменить или удалить её можно в разделе «История».'}
              </Typography>
            )}
          </>
        )}

        {phase === 'progress' && broadcast && (
          <div className={classes.progressWrap}>
            <Typography variant="body2">
              Отправлено {broadcast.sent} из {total}
              {broadcast.failed > 0 && ` · ошибок: ${broadcast.failed}`}
              {broadcast.stopped > 0 && ` · не отправлено: ${broadcast.stopped}`}
            </Typography>
            <LinearProgress
              className={classes.progress}
              variant="determinate"
              value={total > 0 ? Math.min(100, (handled / total) * 100) : 0}
            />
            <Typography className={classes.note}>
              Окно можно закрыть: отправка продолжится в фоне. Ход виден в разделе «История».
            </Typography>
          </div>
        )}

        {phase === 'scheduled' && (
          <>
            <Typography variant="body2" className={classes.resultOk}>
              {broadcast && broadcast.count > 1
                ? `Запланировано отправок: ${broadcast.count}`
                : `Будет отправлено ${summary.sendAt}`}
            </Typography>
            {broadcast && broadcast.count > 1 && (
              <Typography variant="body2" style={{ marginTop: 4 }}>
                {summary.sendAt}
              </Typography>
            )}
            <Typography className={classes.note}>
              Получателей в каждой отправке: {total}. Перед каждой отправкой сервер ещё раз проверит
              согласия и дневной лимит номера. Время можно поменять, а рассылку остановить или удалить
              в разделе «История».
            </Typography>
          </>
        )}

        {phase === 'done' && broadcast && (
          <>
            {broadcast.status === 'done' && (
              <Typography
                variant="body2"
                className={broadcast.failed > 0 ? classes.resultWarn : classes.resultOk}
              >
                Отправлено {broadcast.sent} из {total}
                {broadcast.failed > 0 && `, ошибок: ${broadcast.failed}`}
                {broadcast.stopped > 0 && `, не отправлено: ${broadcast.stopped}`}
              </Typography>
            )}
            {broadcast.status === 'cancelled' && (
              <Typography variant="body2" className={classes.resultWarn}>
                Отменена: отправлено {broadcast.sent}, не отправлено {broadcast.stopped}
              </Typography>
            )}
            {broadcast.status === 'paused' && (
              <>
                <Typography variant="body2" className={classes.resultWarn}>
                  Остановлена: отправлено {broadcast.sent} из {total}
                </Typography>
                <Typography className={classes.note}>
                  Остальным получателям ничего не ушло. Продолжить можно в разделе «История»
                  кнопкой «Запустить».
                </Typography>
              </>
            )}
            {broadcast.status === 'failed' && (
              <Typography variant="body2" className={classes.resultError}>
                Не отправлена: {broadcast.error || 'неизвестная ошибка'}
              </Typography>
            )}
            {(broadcast.failed > 0 || broadcast.stopped > 0) && (
              <Typography className={classes.note}>
                Кому не ушло и почему, видно в разделе «История».
              </Typography>
            )}
          </>
        )}

        {error && (
          <Typography variant="body2" className={classes.resultError}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        {phase === 'confirm' && (
          <>
            <Button onClick={onClose} disabled={busy}>
              Отмена
            </Button>
            <Button
              className={classes.sendButton}
              variant="contained"
              disableElevation
              disabled={busy || blocked || (quietNeeded && !quietOk)}
              startIcon={summary.scheduled ? <ScheduleIcon /> : <SendIcon />}
              onClick={handleConfirm}
            >
              {summary.scheduled ? 'Запланировать' : 'Отправить'}
            </Button>
          </>
        )}
        {phase === 'progress' && (
          <>
            <Button onClick={handleStop} disabled={busy}>
              Остановить рассылку
            </Button>
            <Button variant="outlined" onClick={handleClose}>
              Закрыть
            </Button>
          </>
        )}
        {(phase === 'done' || phase === 'scheduled') && (
          <Button className={classes.sendButton} variant="contained" disableElevation onClick={handleClose}>
            Готово
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default connect(null, mapDispatchToProps)(SendBroadcastDialog);
